import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Search,
  CheckCircle2,
  Trash2,
  Plus,
  Clock,
  Briefcase,
  ShieldAlert,
  Phone,
  Mail,
  Building,
  Check,
  X,
  CreditCard,
  FileText,
  Eye,
  ShieldCheck,
  Landmark,
  UserCheck,
  ClipboardList,
} from 'lucide-react';
import { useState, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { Agent, AgentPayoutStatus } from '@terra/api-client';
import { Badge, Button, EmptyState, Input, Spinner } from '@terra/ui';

import { api } from '../lib/api';

import { AgentDetailsDrawer } from './AgentDetailsDrawer';

function CustomToggle({
  on,
  onClick,
  disabled,
}: {
  on: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-300 disabled:opacity-50 ${on ? 'bg-gold-500' : 'bg-surface-subtle border-border/60 border'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${on ? 'translate-x-4' : 'translate-x-0.5'}`}
      />
    </button>
  );
}

// File-picker styled like the reference "Browse / Choose File" control.
function FilePicker({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (name: string) => void;
}) {
  return (
    <div>
      <label className="text-ink mb-1.5 block text-[13px] font-bold">{label}</label>
      <div className="border-border/60 bg-surface focus-within:border-gold-400 focus-within:ring-gold-400 flex overflow-hidden rounded-xl border transition-all focus-within:ring-1">
        <label className="bg-surface-subtle/50 text-ink hover:bg-surface-subtle border-border/60 cursor-pointer border-r px-4 py-2.5 text-[12px] font-bold transition-colors">
          Browse
          <input
            type="file"
            className="hidden"
            onChange={(e) => onChange(e.target.files?.[0]?.name ?? '')}
          />
        </label>
        <span className="text-ink-subtle flex-1 truncate px-3 py-2.5 text-[12px] font-medium">
          {value || 'Choose File'}
        </span>
      </div>
      <p className="text-ink-muted mt-1 text-[11px] font-semibold">{hint}</p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// AGENT REGISTRATION  — Exported to keep routing intact, but redirects
// or provides the standalone view. We will provide the standalone view
// in the new aesthetic.
// ══════════════════════════════════════════════════════════════════

export function AgentRegistrationPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    alternatePhone: '',
    address: '',
    addressProof: '',
    cancelledCheque: '',
  });

  const create = useMutation({
    mutationFn: () =>
      api.agents.create({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        ...(form.alternatePhone ? { alternatePhone: form.alternatePhone } : {}),
        address: form.address.trim(),
        ...(form.addressProof ? { addressProof: form.addressProof } : {}),
        ...(form.cancelledCheque ? { cancelledCheque: form.cancelledCheque } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agents'] });
      navigate('/agents');
    },
  });

  const valid =
    form.name.length >= 2 &&
    /.+@.+/.test(form.email) &&
    form.phone.length >= 10 &&
    form.address.length >= 4;

  return (
    <div className="max-w-3xl space-y-6 pb-8">
      {/* Topbar renders the title, no local header needed here */}

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm">
        <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
          <Briefcase className="h-4 w-4" /> Agent Information
        </h3>
        <form
          className="grid gap-5 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) create.mutate();
          }}
        >
          <div>
            <Input
              label="Full Name"
              placeholder="e.g. Rahul Sharma"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </div>
          <div>
            <Input
              label="Email Address"
              type="email"
              placeholder="rahul@example.com"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
            />
          </div>
          <div>
            <Input
              label="Phone Number"
              placeholder="10-digit number"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              required
            />
          </div>
          <div>
            <Input
              label="Alternate Phone"
              placeholder="Optional"
              value={form.alternatePhone}
              onChange={(e) => setForm((f) => ({ ...f, alternatePhone: e.target.value }))}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-ink mb-1 block text-[13px] font-bold">
              Address <span className="text-red-500">*</span>
            </label>
            <textarea
              className="border-border/60 bg-surface text-ink placeholder:text-ink-subtle focus:border-gold-400 focus:ring-gold-400 w-full rounded-xl border px-3 py-2.5 text-[13px] font-medium transition-colors focus:outline-none focus:ring-1"
              rows={3}
              placeholder="Enter full address"
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              required
            />
          </div>
          <FilePicker
            label="Address Proof"
            hint="Aadhar card (both sides), Light Bill, Driving License"
            value={form.addressProof}
            onChange={(name) => setForm((f) => ({ ...f, addressProof: name }))}
          />
          <FilePicker
            label="Cancelled Cheque"
            hint="Clear photo of cancelled cheque"
            value={form.cancelledCheque}
            onChange={(name) => setForm((f) => ({ ...f, cancelledCheque: name }))}
          />
          <div className="border-border/40 mt-2 flex items-center justify-end border-t pt-4 sm:col-span-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/agents')}
              className="mr-3"
            >
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending} disabled={!valid} className="px-8">
              Register Agent
            </Button>
          </div>
          {create.isError && (
            <p className="text-right text-[12px] font-semibold text-red-600 sm:col-span-2">
              Could not save — the email or phone may already exist.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

function AgentOpsTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Field Agents states
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  const agents = useQuery({
    queryKey: ['agents', search],
    queryFn: () => api.agents.list(search ? { q: search } : undefined),
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.agents.update>[1] }) =>
      api.agents.update(id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['agents'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.agents.remove(id),
    onSuccess: () => {
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });

  const rows = agents.data ?? [];
  const allChecked = rows.length > 0 && selected.size === rows.length;

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Add agent form state (for inline form)
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });

  const createInline = useMutation({
    mutationFn: () =>
      api.agents.create({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
      }),
    onSuccess: () => {
      setForm({ name: '', email: '', phone: '', address: '' });
      setShowAdd(false);
      void qc.invalidateQueries({ queryKey: ['agents'] });
    },
  });

  return (
    <Fragment>
      <div className="space-y-5">
        <AnimatePresence>
          {showAdd && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-ink-muted flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
                  <Briefcase className="h-4 w-4" /> Quick Create Agent
                </h3>
                <button
                  type="button"
                  onClick={() => navigate('/agents/new')}
                  className="text-gold-600 hover:text-gold-700 text-[12px] font-bold transition-colors"
                >
                  Open Full Form &rarr;
                </button>
              </div>

              <form
                className="grid items-start gap-4 sm:grid-cols-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (form.name && form.email && form.phone && form.address) createInline.mutate();
                }}
              >
                <Input
                  label="Name"
                  placeholder="Agent Name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
                <Input
                  label="Email"
                  type="email"
                  placeholder="agent@example.com"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
                <Input
                  label="Phone"
                  placeholder="10-digit number"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  required
                />
                <div className="flex flex-col gap-1">
                  <Input
                    label="Address City"
                    placeholder="e.g. Mumbai"
                    value={form.address}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    required
                  />
                </div>
                <div className="flex justify-end sm:col-span-4">
                  <Button type="submit" loading={createInline.isPending} className="h-10">
                    Create Agent
                  </Button>
                </div>
                {createInline.isError && (
                  <p className="text-right text-[12px] font-semibold text-red-600 sm:col-span-4">
                    Could not create — check email/phone uniqueness.
                  </p>
                )}
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
          {/* Toolbar */}
          <div className="border-border/60 bg-surface-subtle/30 flex flex-col flex-wrap items-center justify-between gap-4 border-b px-5 py-4 sm:flex-row">
            <div className="flex items-center gap-3">
              <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                <Briefcase className="h-4 w-4" strokeWidth={2} />
              </div>
              {selected.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-ink-muted border-border/60 border-r px-2 text-[12px] font-bold">
                    {selected.size} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Delete ${selected.size} agent(s)?`)) {
                        [...selected].forEach((id) => remove.mutate(id));
                      }
                    }}
                    className="flex h-9 items-center justify-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 text-[12px] font-bold text-red-600 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-red-100 hover:shadow-md"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              )}
            </div>

            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <div className="relative w-full sm:w-56">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Search className="text-ink-muted h-4 w-4" strokeWidth={2} />
                </div>
                <input
                  type="text"
                  placeholder="Search by name, email or code..."
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setSearch(searchDraft.trim());
                  }}
                  className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 placeholder:text-ink-subtle h-9 w-full rounded-xl border pl-9 pr-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
                />
              </div>
              <button
                type="button"
                onClick={() => setSearch(searchDraft.trim())}
                className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center rounded-xl px-4 text-[12px] font-bold text-white shadow-sm transition-all sm:w-auto"
              >
                Search
              </button>
              <div className="bg-border/60 mx-1 hidden h-6 w-px sm:block" />
              <button
                type="button"
                onClick={() => navigate('/agents/payout-time')}
                className="border-border/60 bg-surface text-ink hover:bg-surface-subtle flex h-9 items-center justify-center gap-2 rounded-xl border px-4 text-[12px] font-bold shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <Clock className="h-4 w-4" /> Payout Settings
              </button>
              <button
                type="button"
                onClick={() => setShowAdd((v) => !v)}
                className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center rounded-xl px-4 text-[12px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                {showAdd ? 'Close Form' : '+ Add New Agent'}
              </button>
            </div>
          </div>

          {/* Data Table */}
          <div className="p-0">
            {agents.isLoading ? (
              <div className="grid min-h-[300px] place-items-center">
                <Spinner size="lg" />
              </div>
            ) : rows.length === 0 ? (
              <div className="py-12">
                <EmptyState
                  icon={<Briefcase className="text-ink-muted h-10 w-10" />}
                  title="No agents found"
                  description="Agents matching your criteria will appear here."
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                      <th className="py-3 pl-5 pr-3">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          onChange={() =>
                            setSelected(allChecked ? new Set() : new Set(rows.map((a) => a.id)))
                          }
                          className="accent-gold-500 h-4 w-4 cursor-pointer rounded border-gray-300"
                        />
                      </th>
                      <th className="py-3 pr-4">Agent Details</th>
                      <th className="py-3 pr-4">Contact</th>
                      <th className="py-3 pr-4 text-center">Code</th>
                      <th className="py-3 pr-4 text-center">KYC</th>
                      <th className="py-3 pr-4 text-right">Customers</th>
                      <th className="py-3 pr-4 text-right">Commission</th>
                      <th className="py-3 pr-4 text-center">Approved</th>
                      <th className="py-3 pr-4 text-center">Status</th>
                      <th className="py-3 pl-4 pr-5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-border/40 divide-y text-[13px]">
                    <AnimatePresence>
                      {rows.map((a: Agent, idx: number) => {
                        const isActive = a.status === 'ACTIVE';
                        return (
                          <motion.tr
                            key={a.id}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.02 }}
                            className="hover:bg-surface-subtle/30 group transition-colors"
                          >
                            <td className="py-3 pl-5 pr-3">
                              <input
                                type="checkbox"
                                checked={selected.has(a.id)}
                                onChange={() => toggleRow(a.id)}
                                className="accent-gold-500 h-4 w-4 cursor-pointer rounded border-gray-300"
                              />
                            </td>
                            <td className="py-3 pr-4">
                              <div className="flex items-center gap-3">
                                <div className="bg-surface-subtle text-ink-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold">
                                  {a.name.slice(0, 1).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-ink truncate font-semibold">{a.name}</p>
                                  <p className="text-ink-subtle truncate text-xs">{a.address}</p>
                                </div>
                              </div>
                            </td>
                            <td className="text-ink-muted py-3 pr-4">
                              <div className="space-y-0.5 text-[12px]">
                                <p className="flex items-center gap-1.5">
                                  <Phone className="text-ink-subtle h-3 w-3" />
                                  {a.phone}
                                </p>
                                <p className="flex items-center gap-1.5">
                                  <Mail className="text-ink-subtle h-3 w-3" />
                                  <span className="max-w-[140px] truncate">{a.email}</span>
                                </p>
                              </div>
                            </td>
                            <td className="py-3 pr-4 text-center">
                              <span className="bg-surface-subtle text-ink-muted border-border/40 inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-mono text-[11px] font-bold">
                                {a.agentCode}
                              </span>
                            </td>
                            <td className="py-3 pr-4 text-center">
                              {a.user ? (
                                <Badge
                                  tone={
                                    a.user.kycStatus === 'APPROVED'
                                      ? 'success'
                                      : a.user.kycStatus === 'REJECTED'
                                        ? 'danger'
                                        : 'warning'
                                  }
                                >
                                  {a.user.kycStatus.replace(/_/g, ' ')}
                                </Badge>
                              ) : (
                                <span
                                  className="text-ink-subtle text-[11px]"
                                  title="No login linked"
                                >
                                  No login
                                </span>
                              )}
                            </td>
                            <td className="text-ink py-3 pr-4 text-right font-semibold">
                              {a._count?.leads ?? 0}
                            </td>
                            <td className="py-3 pr-4 text-right">
                              <button
                                type="button"
                                className="text-ink font-bold underline decoration-dotted underline-offset-2"
                                title="Change commission rate"
                                onClick={() => {
                                  const input = window.prompt(
                                    `Commission % for ${a.name} (e.g. 5 or 2.5). Applies to orders that settle from now on.`,
                                    String(a.commissionBps / 100),
                                  );
                                  if (input === null) return;
                                  const pct = Number(input);
                                  if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
                                    window.alert('Enter a percentage from 0 to 100.');
                                    return;
                                  }
                                  update.mutate({
                                    id: a.id,
                                    body: { commissionBps: Math.round(pct * 100) },
                                  });
                                }}
                              >
                                {(a.commissionBps / 100).toFixed(2)}%
                              </button>
                            </td>
                            <td className="py-3 pr-4 text-center">
                              <CustomToggle
                                on={a.approved}
                                disabled={update.isPending}
                                onClick={() =>
                                  update.mutate({ id: a.id, body: { approved: !a.approved } })
                                }
                              />
                            </td>
                            <td className="py-3 pr-4 text-center">
                              <Badge tone={isActive ? 'success' : 'neutral'}>
                                {isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="py-3 pl-4 pr-5">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => setSelectedAgentId(a.id)}
                                  className="text-ink-muted hover:bg-surface-subtle hover:text-ink flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                                  title="View Agent Details"
                                >
                                  <Eye className="h-[18px] w-[18px]" strokeWidth={2} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    update.mutate({
                                      id: a.id,
                                      body: { status: isActive ? 'INACTIVE' : 'ACTIVE' },
                                    })
                                  }
                                  className={`${
                                    isActive
                                      ? 'text-red-500 hover:bg-red-50'
                                      : 'text-green-600 hover:bg-green-50'
                                  } flex h-8 w-8 items-center justify-center rounded-lg transition-colors`}
                                  title={isActive ? 'Deactivate' : 'Activate'}
                                >
                                  {isActive ? (
                                    <X className="h-[18px] w-[18px]" strokeWidth={2.5} />
                                  ) : (
                                    <Check className="h-[18px] w-[18px]" strokeWidth={2.5} />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Remove ${a.name} from the agent programme? New orders stop earning them commission; what they have already earned and their payout history are kept.`,
                                      )
                                    )
                                      remove.mutate(a.id);
                                  }}
                                  className="text-ink-muted flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                                  title="Delete Agent"
                                >
                                  <Trash2 className="h-[18px] w-[18px]" strokeWidth={2} />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {selectedAgentId && (
          <AgentDetailsDrawer agentId={selectedAgentId} onClose={() => setSelectedAgentId(null)} />
        )}
      </AnimatePresence>
    </Fragment>
  );
}

function AgentComplianceTab() {
  const qc = useQueryClient();

  const [affiliateSearchDraft, setAffiliateSearchDraft] = useState('');
  const [affiliateSearch, setAffiliateSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState('');

  const affiliates = useQuery({
    queryKey: ['affiliate-agents', affiliateSearch],
    queryFn: () =>
      api.users.listUsers({ role: 'REFERRAL_AGENT', q: affiliateSearch, pageSize: 100 }),
  });

  const userKyc = useQuery({
    queryKey: ['user-kyc-detail', selectedUserId],
    queryFn: () => api.users.adminGetUserKyc(selectedUserId!),
    enabled: !!selectedUserId,
  });

  const reviewUserKyc = useMutation({
    mutationFn: ({
      userId,
      status,
      note,
    }: {
      userId: string;
      status: 'APPROVED' | 'REJECTED';
      note?: string;
    }) => api.users.adminReviewUserKyc(userId, { status, ...(note ? { reviewNote: note } : {}) }),
    onSuccess: () => {
      setSelectedUserId(null);
      setReviewNote('');
      void qc.invalidateQueries({ queryKey: ['affiliate-agents'] });
    },
  });

  const affiliateRows = affiliates.data?.data ?? [];

  return (
    <div className="space-y-5">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {/* Toolbar */}
        <div className="border-border/60 bg-surface-subtle/30 flex flex-col flex-wrap items-center justify-between gap-4 border-b px-5 py-4 sm:flex-row">
          <div className="flex items-center gap-3">
            <span className="text-ink-muted text-[12px] font-bold">
              Commerce Partners Onboarding & KYC Queue
            </span>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <div className="relative w-full sm:w-64">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="text-ink-muted h-4 w-4" strokeWidth={2} />
              </div>
              <input
                type="text"
                placeholder="Search affiliates by name, email..."
                value={affiliateSearchDraft}
                onChange={(e) => setAffiliateSearchDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setAffiliateSearch(affiliateSearchDraft.trim());
                }}
                className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 placeholder:text-ink-subtle h-9 w-full rounded-xl border pl-9 pr-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
              />
            </div>
            <button
              type="button"
              onClick={() => setAffiliateSearch(affiliateSearchDraft.trim())}
              className="bg-ink hover:bg-ink/90 flex h-9 w-full items-center justify-center rounded-xl px-4 text-[12px] font-bold text-white shadow-sm transition-all sm:w-auto"
            >
              Search
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div className="p-0">
          {affiliates.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : affiliateRows.length === 0 ? (
            <div className="py-12">
              <EmptyState
                icon={<Users className="text-ink-muted h-10 w-10" />}
                title="No affiliate partners found"
                description="Referral agents applying for onboarding will appear here."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="py-3 pl-6 pr-4">Partner Details</th>
                    <th className="py-3 pr-4">Contact</th>
                    <th className="py-3 pr-4 text-center">Status</th>
                    <th className="py-3 pr-4 text-center">KYC Compliance</th>
                    <th className="py-3 pr-4">Date Registered</th>
                    <th className="py-3 pl-4 pr-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  <AnimatePresence>
                    {affiliateRows.map((u: any, idx: number) => {
                      const isActive = u.status === 'ACTIVE';
                      return (
                        <motion.tr
                          key={u.id}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.02 }}
                          className="hover:bg-surface-subtle/30 group transition-colors"
                        >
                          <td className="py-3 pl-6 pr-4">
                            <div className="flex items-center gap-3">
                              <div className="bg-surface-subtle text-ink-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold">
                                {u.name.slice(0, 1).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className="text-ink truncate font-semibold">{u.name}</p>
                                <p className="text-ink-subtle truncate text-xs">
                                  Roles: {u.roles.join(', ')}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="text-ink-muted py-3 pr-4">
                            <div className="space-y-0.5 text-[12px]">
                              <p className="flex items-center gap-1.5">
                                <Phone className="text-ink-subtle h-3 w-3" />
                                {u.phone}
                              </p>
                              <p className="flex items-center gap-1.5">
                                <Mail className="text-ink-subtle h-3 w-3" />
                                <span className="max-w-[140px] truncate">{u.email}</span>
                              </p>
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-center">
                            <Badge tone={isActive ? 'success' : 'neutral'}>{u.status}</Badge>
                          </td>
                          <td className="py-3 pr-4 text-center">
                            <Badge
                              tone={
                                u.kycStatus === 'APPROVED'
                                  ? 'success'
                                  : u.kycStatus === 'REJECTED'
                                    ? 'danger'
                                    : 'warning'
                              }
                            >
                              {u.kycStatus}
                            </Badge>
                          </td>
                          <td className="py-3 pr-4 text-slate-500">
                            {new Date(u.createdAt).toLocaleDateString()}
                          </td>
                          <td className="py-3 pl-4 pr-6">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setSelectedUserId(u.id)}
                                className="text-ink-muted hover:text-ink hover:bg-surface-subtle flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                                title="View & Review KYC Compliance Documents"
                              >
                                <Eye className="h-[18px] w-[18px]" strokeWidth={2} />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── KYC Compliance Slide-over drawer for Referral Agents ── */}
      <AnimatePresence>
        {selectedUserId && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedUserId(null)}
              className="bg-ink/40 absolute inset-0 backdrop-blur-sm"
            />
            <motion.div
              initial={{ x: '100%', opacity: 0.5 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0.5 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="border-border/60 bg-surface relative flex h-full w-full max-w-3xl flex-col border-l shadow-2xl"
            >
              {/* Header */}
              <div className="border-border/60 bg-surface/80 sticky top-0 z-10 flex items-center justify-between border-b px-6 py-4 backdrop-blur-md">
                <div>
                  <h3 className="text-ink text-lg font-bold tracking-tight">
                    Agent KYC & Bank Review
                  </h3>
                  <p className="text-ink-subtle text-xs font-medium">
                    Verify affiliate compliance documents and bank ledger payout routes
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedUserId(null)}
                  className="bg-surface-subtle text-ink-muted hover:text-ink hover:bg-surface-subtle/80 flex h-8 w-8 items-center justify-center rounded-full transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-5">
                {userKyc.isLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <Spinner size="lg" />
                  </div>
                ) : !userKyc.data ? (
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <ShieldAlert className="mb-2 h-10 w-10 text-red-400" />
                    <p className="text-sm font-medium text-red-500">
                      Error loading compliance documents.
                    </p>
                  </div>
                ) : (
                  (() => {
                    const kyc = userKyc.data;
                    return (
                      <div className="space-y-4 text-sm">
                        {/* Status banner */}
                        <div
                          className={`flex items-center justify-between gap-4 rounded-xl border px-3 py-2 ${kyc.status === 'APPROVED' ? 'border-emerald-200/50 bg-emerald-50/50 text-emerald-800' : kyc.status === 'REJECTED' ? 'border-rose-200/50 bg-rose-50/50 text-rose-800' : 'border-amber-200/50 bg-amber-50/50 text-amber-800'}`}
                        >
                          <div className="flex items-center gap-1.5 text-xs font-bold">
                            {kyc.status === 'APPROVED' ? (
                              <ShieldCheck className="h-4.5 w-4.5 text-emerald-600" />
                            ) : (
                              <ShieldAlert className="h-4.5 w-4.5 text-amber-500" />
                            )}
                            <span>KYC Status: {kyc.status}</span>
                          </div>
                          {kyc.reviewNote && (
                            <span
                              className="max-w-[300px] truncate text-xs font-medium opacity-90"
                              title={kyc.reviewNote}
                            >
                              Compliance note: {kyc.reviewNote}
                            </span>
                          )}
                        </div>

                        {/* Two column details grid */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          {/* Column 1: Identity & Tax Details & Attachments */}
                          <div className="space-y-4">
                            {/* Identity section */}
                            <section className="space-y-2">
                              <h4 className="text-ink-muted flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                                <CreditCard className="h-3.5 w-3.5" /> Identity & Tax Details
                              </h4>
                              <div className="bg-surface-subtle/30 border-border/60 rounded-2xl border px-4 py-1.5">
                                <div className="border-border/40 flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
                                  <span className="text-ink-muted shrink-0 text-xs font-semibold">
                                    Legal Name
                                  </span>
                                  <span
                                    className="text-ink max-w-[180px] break-all text-right text-xs font-semibold"
                                    title={kyc.legalName || '—'}
                                  >
                                    {kyc.legalName || '—'}
                                  </span>
                                </div>
                                <div className="border-border/40 flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
                                  <span className="text-ink-muted shrink-0 text-xs font-semibold">
                                    PAN Number
                                  </span>
                                  <span className="text-ink break-all text-right font-mono text-xs font-semibold">
                                    {kyc.pan || '—'}
                                  </span>
                                </div>
                                <div className="border-border/40 flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
                                  <span className="text-ink-muted shrink-0 text-xs font-semibold">
                                    Aadhaar (PII)
                                  </span>
                                  <span className="text-ink break-all text-right font-mono text-xs font-semibold">
                                    {kyc.aadhaar || 'Not Provided'}
                                  </span>
                                </div>
                              </div>
                            </section>

                            {/* Documents section */}
                            <section className="space-y-2">
                              <h4 className="text-ink-muted flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                                <FileText className="h-3.5 w-3.5" /> Submitted Attachments
                              </h4>
                              <div className="space-y-2.5">
                                {/* PAN Document Item */}
                                <div className="border-border/60 bg-surface-subtle/20 rounded-xl border p-2.5 text-xs">
                                  <div className="flex items-center justify-between">
                                    <span className="text-ink flex items-center gap-1.5 truncate font-semibold">
                                      <FileText className="text-gold-500 h-3.5 w-3.5 shrink-0" />{' '}
                                      PAN Card Photo
                                    </span>
                                    {kyc.panDocUrl ? (
                                      <a
                                        href={kyc.panDocUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-amber-600 hover:text-amber-700"
                                      >
                                        View Full size
                                      </a>
                                    ) : (
                                      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                        Not uploaded
                                      </span>
                                    )}
                                  </div>
                                  {kyc.panDocUrl && (
                                    <div className="mt-2 flex aspect-[4/3] max-h-[140px] items-center justify-center overflow-hidden rounded-lg border bg-white">
                                      <img
                                        src={kyc.panDocUrl}
                                        alt="PAN card document"
                                        className="h-full w-full object-contain"
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* Address Proof Document Item */}
                                <div className="border-border/60 bg-surface-subtle/20 rounded-xl border p-2.5 text-xs">
                                  <div className="flex items-center justify-between">
                                    <span className="text-ink flex items-center gap-1.5 truncate font-semibold">
                                      <FileText className="text-gold-500 h-3.5 w-3.5 shrink-0" />{' '}
                                      Aadhaar / Address Proof
                                    </span>
                                    {kyc.addressProofDocUrl ? (
                                      <a
                                        href={kyc.addressProofDocUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-amber-600 hover:text-amber-700"
                                      >
                                        View Full size
                                      </a>
                                    ) : (
                                      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                        Not uploaded
                                      </span>
                                    )}
                                  </div>
                                  {kyc.addressProofDocUrl && (
                                    <div className="mt-2 flex aspect-[4/3] max-h-[140px] items-center justify-center overflow-hidden rounded-lg border bg-white">
                                      <img
                                        src={kyc.addressProofDocUrl}
                                        alt="Address proof document"
                                        className="h-full w-full object-contain"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </section>
                          </div>

                          {/* Column 2: Bank Account Details */}
                          <div className="space-y-4">
                            <section className="space-y-2">
                              <h4 className="text-ink-muted flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                                <Landmark className="h-3.5 w-3.5" /> Bank Account Details
                              </h4>
                              <div className="bg-surface-subtle/30 border-border/60 rounded-2xl border px-4 py-1.5">
                                <div className="border-border/40 flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
                                  <span className="text-ink-muted shrink-0 text-xs font-semibold">
                                    Account Holder
                                  </span>
                                  <span
                                    className="text-ink max-w-[180px] break-all text-right text-xs font-semibold"
                                    title={kyc.bankAccountName || '—'}
                                  >
                                    {kyc.bankAccountName || '—'}
                                  </span>
                                </div>
                                <div className="border-border/40 flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
                                  <span className="text-ink-muted shrink-0 text-xs font-semibold">
                                    Account Number
                                  </span>
                                  <span className="text-ink break-all text-right font-mono text-xs font-semibold">
                                    {kyc.bankAccountNumber || '—'}
                                  </span>
                                </div>
                                <div className="border-border/40 flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
                                  <span className="text-ink-muted shrink-0 text-xs font-semibold">
                                    Bank IFSC Code
                                  </span>
                                  <span className="text-ink break-all text-right font-mono text-xs font-semibold">
                                    {kyc.bankIfsc || '—'}
                                  </span>
                                </div>
                                <div className="border-border/40 flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
                                  <span className="text-ink-muted shrink-0 text-xs font-semibold">
                                    UPI Identifier
                                  </span>
                                  <span className="text-ink break-all text-right font-mono text-xs font-bold text-amber-600">
                                    {kyc.upiId || 'Not Provided'}
                                  </span>
                                </div>
                                <div className="border-border/40 flex items-start justify-between gap-4 border-b py-2 last:border-b-0">
                                  <span className="text-ink-muted shrink-0 text-xs font-semibold">
                                    Payout Mode
                                  </span>
                                  <span className="text-ink break-all text-right text-xs font-semibold">
                                    {kyc.payoutPreference || 'BANK'}
                                  </span>
                                </div>
                              </div>
                            </section>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>

              {/* Drawer actions */}
              {userKyc.data && (
                <div className="border-border/60 bg-surface sticky bottom-0 border-t p-4 shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)]">
                  <label className="text-ink-muted mb-1.5 block text-[11px] font-bold uppercase tracking-wider">
                    Review Compliance Note
                  </label>
                  <textarea
                    placeholder="Provide reason for approval/rejection..."
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    className="border-border bg-surface text-ink focus:border-gold-500 focus:ring-gold-500/20 mb-3 h-[50px] min-h-[50px] w-full resize-none rounded-xl border p-2 text-xs outline-none transition-all focus:ring-2"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      disabled={reviewUserKyc.isPending}
                      onClick={() => {
                        if (
                          window.confirm('Are you sure you want to reject this KYC application?')
                        ) {
                          reviewUserKyc.mutate({
                            userId: selectedUserId,
                            status: 'REJECTED',
                            note:
                              reviewNote.trim() ||
                              'Verification failed. Incorrect PAN/Bank details.',
                          });
                        }
                      }}
                    >
                      Reject KYC
                    </Button>
                    <Button
                      disabled={reviewUserKyc.isPending}
                      onClick={() =>
                        reviewUserKyc.mutate({
                          userId: selectedUserId,
                          status: 'APPROVED',
                          note: reviewNote.trim() || 'Identity & bank ledger routes approved.',
                        })
                      }
                    >
                      Approve & Auto-Code
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

type AgentTab = 'ops' | 'compliance';

const AGENT_TABS: { id: AgentTab; label: string; icon: React.ReactNode; description: string }[] = [
  {
    id: 'ops',
    label: 'All Agents',
    icon: <UserCheck className="h-4 w-4" />,
    description: 'Manage field agents list and active transaction settings',
  },
  {
    id: 'compliance',
    label: 'KYC Pending',
    icon: <ClipboardList className="h-4 w-4" />,
    description: 'Verify affiliate compliance documents and KYC submissions',
  },
];

export function AllAgentsPage({ initialTab = 'ops' }: { initialTab?: AgentTab }) {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<AgentTab>(
    tabParam === 'compliance' ? 'compliance' : tabParam === 'ops' ? 'ops' : initialTab,
  );

  return (
    <div className="space-y-6 pb-8">
      {/* Tab bar */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="flex">
          {AGENT_TABS.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`group relative flex flex-1 flex-col items-start gap-0.5 border-b-2 px-6 py-4 text-left transition-all ${
                  active ? 'border-ink bg-surface' : 'hover:bg-surface-subtle/50 border-transparent'
                }`}
              >
                <span
                  className={`flex items-center gap-2 text-[13px] font-bold transition-colors ${
                    active ? 'text-ink' : 'text-ink-muted group-hover:text-ink'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </span>
                <span className="text-ink-subtle text-[11px] font-medium leading-snug">
                  {tab.description}
                </span>
                {active && (
                  <motion.span
                    layoutId="agent-tab-indicator"
                    className="bg-ink absolute bottom-0 left-0 right-0 h-[2px] rounded-t"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.18 }}
        >
          {activeTab === 'ops' ? <AgentOpsTab /> : <AgentComplianceTab />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export function CommercePartnersPage() {
  return <AllAgentsPage initialTab="compliance" />;
}

// ══════════════════════════════════════════════════════════════════
// SET PAYOUT TIME  — "Agent Payout Time"
// ══════════════════════════════════════════════════════════════════

const rupees = (paise: number) =>
  `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function apiError(err: unknown): string {
  const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
  return e.response?.data?.error?.message ?? e.message ?? 'That did not work.';
}

/**
 * Agent payouts: when they are made, and the queue of payouts to send.
 *
 * On the payout day the system puts each agent's unpaid commission (minus TDS
 * under 194H) into a payout; agents can also ask for one. Paying is done
 * outside the system — a bank or UPI transfer — and recorded here with its UTR.
 */
export function AgentPayoutTimePage() {
  const qc = useQueryClient();
  const setting = useQuery({
    queryKey: ['agent-payout-setting'],
    queryFn: () => api.agents.getPayoutSetting(),
  });

  const [dueDate, setDueDate] = useState('');
  const [day, setDay] = useState('');
  const [minRupees, setMinRupees] = useState('');
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [hydrated, setHydrated] = useState(false);
  const [queueStatus, setQueueStatus] = useState<AgentPayoutStatus>('PENDING');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  if (setting.data && !hydrated) {
    setDueDate(setting.data.payoutDueDate ?? '');
    setDay(setting.data.payoutDayOfMonth ? String(setting.data.payoutDayOfMonth) : '');
    setMinRupees(String(setting.data.minPayoutPaise / 100));
    setStatus(setting.data.status);
    setHydrated(true);
  }

  const dayNum = day ? Number(day) : null;
  const minPaise = Math.round(Number(minRupees) * 100);
  const valid =
    (dayNum === null || (Number.isInteger(dayNum) && dayNum >= 1 && dayNum <= 28)) &&
    Number.isFinite(minPaise) &&
    minPaise >= 100;

  const save = useMutation({
    mutationFn: () =>
      api.agents.updatePayoutSetting({
        ...(dueDate ? { payoutDueDate: dueDate } : {}),
        payoutDayOfMonth: dayNum,
        minPayoutPaise: minPaise,
        status,
      }),
    onSuccess: () => {
      setNotice({ tone: 'ok', text: 'Payout schedule saved.' });
      void qc.invalidateQueries({ queryKey: ['agent-payout-setting'] });
    },
    onError: (err) => setNotice({ tone: 'error', text: apiError(err) }),
  });

  const payouts = useQuery({
    queryKey: ['agent-payouts-admin', queueStatus],
    queryFn: () => api.agents.listAllPayouts({ status: queueStatus }),
  });
  const refreshQueue = () => void qc.invalidateQueries({ queryKey: ['agent-payouts-admin'] });

  const runNow = useMutation({
    mutationFn: () => api.agents.runPayouts(),
    onSuccess: (r) => {
      setNotice({
        tone: 'ok',
        text: `${r.created} payout${r.created === 1 ? '' : 's'} created${
          r.skipped
            ? `; ${r.skipped} agent(s) skipped (below minimum, KYC or approval pending, or a payout already open)`
            : ''
        }.`,
      });
      refreshQueue();
    },
    onError: (err) => setNotice({ tone: 'error', text: apiError(err) }),
  });

  const markPaid = useMutation({
    mutationFn: (v: { id: string; reference: string }) =>
      api.agents.markPayoutPaid(v.id, { reference: v.reference }),
    onSuccess: () => {
      setNotice({ tone: 'ok', text: 'Marked as paid. The agent sees the UTR on their dashboard.' });
      refreshQueue();
    },
    onError: (err) => setNotice({ tone: 'error', text: apiError(err) }),
  });

  const cancel = useMutation({
    mutationFn: (v: { id: string; note: string }) =>
      api.agents.cancelPayout(v.id, { note: v.note }),
    onSuccess: () => {
      setNotice({ tone: 'ok', text: 'Payout cancelled; the commission goes back to unpaid.' });
      refreshQueue();
    },
    onError: (err) => setNotice({ tone: 'error', text: apiError(err) }),
  });

  const rows = payouts.data ?? [];
  const totalToPay = rows.reduce((n, p) => n + p.amountPaise, 0);

  return (
    <div className="max-w-5xl space-y-6 pb-8">
      {notice && (
        <p
          role="status"
          className={`rounded-xl px-4 py-3 text-[13px] font-semibold ${
            notice.tone === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'
          }`}
        >
          {notice.text}
        </p>
      )}

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 border-b px-6 py-5">
          <h3 className="text-ink flex items-center gap-2 text-[14px] font-bold">
            <Clock className="text-gold-500 h-4 w-4" /> Payout Schedule
          </h3>
        </div>
        <div className="p-6">
          {setting.isLoading ? (
            <div className="grid min-h-[160px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="text-ink mb-1.5 block text-[13px] font-bold">
                    Payout day of the month
                  </label>
                  <p className="text-ink-muted mb-2 text-[11px] font-medium">
                    1 to 28. On this day payouts are created for every approved agent with KYC
                    approved and at least the minimum unpaid. Leave blank to pay only on request.
                  </p>
                  <Input
                    type="number"
                    min={1}
                    max={28}
                    placeholder="e.g. 5"
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-ink mb-1.5 block text-[13px] font-bold">
                    Minimum payout (₹)
                  </label>
                  <p className="text-ink-muted mb-2 text-[11px] font-medium">
                    Smaller balances roll over to the next month.
                  </p>
                  <Input
                    type="number"
                    min={1}
                    value={minRupees}
                    onChange={(e) => setMinRupees(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-ink mb-1.5 block text-[13px] font-bold">
                    Note shown to agents
                  </label>
                  <p className="text-ink-muted mb-2 text-[11px] font-medium">
                    Optional, e.g. “Paid within 3 working days”.
                  </p>
                  <Input value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </div>
                <div>
                  <label className="text-ink mb-1.5 block text-[13px] font-bold">
                    Automatic payouts
                  </label>
                  <p className="text-ink-muted mb-2 text-[11px] font-medium">
                    Inactive stops the monthly run; agents can still request a payout.
                  </p>
                  <select
                    className="border-border/60 bg-surface text-ink h-10 w-full rounded-xl border px-3 text-[13px] font-medium"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as 'ACTIVE' | 'INACTIVE')}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="border-border/40 flex items-center justify-between gap-3 border-t pt-4">
                {!valid ? (
                  <span className="text-[12px] font-semibold text-red-600">
                    Day must be 1–28 and the minimum at least ₹1.
                  </span>
                ) : (
                  <span />
                )}
                <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>
                  Save schedule
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex flex-wrap items-center justify-between gap-3 border-b px-6 py-5">
          <h3 className="text-ink flex items-center gap-2 text-[14px] font-bold">
            <Landmark className="text-gold-500 h-4 w-4" /> Agent payouts
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="border-border/60 bg-surface text-ink h-9 rounded-xl border px-3 text-[13px] font-medium"
              value={queueStatus}
              onChange={(e) => setQueueStatus(e.target.value as AgentPayoutStatus)}
            >
              <option value="PENDING">To pay</option>
              <option value="PAID">Paid</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            <Button
              variant="outline"
              loading={runNow.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Create payouts now for every agent owed at least the minimum? Agents with a payout already open are skipped.',
                  )
                )
                  runNow.mutate();
              }}
            >
              Run payouts now
            </Button>
          </div>
        </div>
        {queueStatus === 'PENDING' && rows.length > 0 && (
          <p className="text-ink-muted border-border/60 border-b px-6 py-3 text-[12px] font-medium">
            {rows.length} to pay · {rupees(totalToPay)} in total. Transfer each amount, then record
            its UTR.
          </p>
        )}
        {payouts.isLoading ? (
          <div className="grid min-h-[160px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10">
            <EmptyState
              icon={<Landmark className="text-ink-muted h-10 w-10" />}
              title="Nothing here"
              description={
                queueStatus === 'PENDING'
                  ? 'No payouts are waiting. They appear on the payout day, when an agent asks, or when you run payouts now.'
                  : 'No payouts with this status yet.'
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-5 py-3">Agent</th>
                  <th className="px-3 py-3">Pay to</th>
                  <th className="px-3 py-3 text-right">Commission</th>
                  <th className="px-3 py-3 text-right">TDS</th>
                  <th className="px-3 py-3 text-right">Transfer</th>
                  <th className="px-3 py-3">Created</th>
                  <th className="px-5 py-3 text-right">
                    {queueStatus === 'PENDING' ? 'Actions' : 'Reference'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y">
                {rows.map((p) => (
                  <tr key={p.id} className="align-top">
                    <td className="px-5 py-3">
                      <p className="text-ink font-semibold">{p.agent.name}</p>
                      <p className="text-ink-muted font-mono text-[11px]">
                        {p.agent.agentCode} · {p.agent.phone}
                      </p>
                      <p className="text-ink-subtle text-[11px]">
                        {p.source === 'REQUEST'
                          ? 'Requested by agent'
                          : p.source === 'SCHEDULE'
                            ? 'Monthly run'
                            : 'Created by admin'}
                        {p.financialYear ? ` · FY ${p.financialYear}` : ''}
                      </p>
                    </td>
                    <td className="px-3 py-3 text-[12px]">
                      {p.destination ? (
                        p.method === 'UPI' && p.destination.upiId ? (
                          <p className="text-ink font-mono">UPI {p.destination.upiId}</p>
                        ) : (
                          <>
                            <p className="text-ink font-semibold">{p.destination.accountName}</p>
                            <p className="text-ink-muted font-mono">
                              A/c ••••{p.destination.accountLast4 ?? '????'} · {p.destination.ifsc}
                            </p>
                          </>
                        )
                      ) : (
                        <span className="text-red-600">No KYC on file</span>
                      )}
                    </td>
                    <td className="text-ink px-3 py-3 text-right">{rupees(p.grossPaise)}</td>
                    <td className="text-ink-muted px-3 py-3 text-right">
                      {p.tdsPaise ? rupees(p.tdsPaise) : '—'}
                    </td>
                    <td className="text-ink px-3 py-3 text-right font-bold">
                      {rupees(p.amountPaise)}
                    </td>
                    <td className="text-ink-muted px-3 py-3">
                      {new Date(p.createdAt).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {queueStatus === 'PENDING' ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            disabled={markPaid.isPending}
                            onClick={() => {
                              const reference = window.prompt(
                                `Transferred ${rupees(p.amountPaise)} to ${p.agent.name}? Enter the bank/UPI transaction reference (UTR):`,
                              );
                              if (reference && reference.trim().length >= 4) {
                                markPaid.mutate({ id: p.id, reference: reference.trim() });
                              }
                            }}
                          >
                            Mark paid
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={cancel.isPending}
                            onClick={() => {
                              const note = window.prompt(
                                'Why is this payout cancelled? The commission returns to unpaid and the agent sees this note.',
                              );
                              if (note && note.trim().length >= 4)
                                cancel.mutate({ id: p.id, note: note.trim() });
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <span className="text-ink-muted font-mono text-[12px]">
                          {p.reference ?? p.note ?? '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
